// THE FORGE hologram: press and hold the focused card and its figure lifts out
// of it — the Collection viewer's HOLO V2 (attachCardHolo2: baked matte + plate,
// peel style, light column), drawn by a short-lived transparent WebGL canvas laid
// exactly over the DOM card's art window. Exists only while held / settling:
// release lowers the figure, then the canvas and its context are disposed.
// Presentation only: never touches the game rng.
import * as THREE from 'three';

const ART_W = 1.26 * 0.94, ART_H = 1.76 * 0.94;   // holo2's default art window (card units)

/**
 * @param root   overlay parent (the forge)
 * @param d      card data
 * @param url    art image URL (the full art when already loaded)
 * @param rect   the DOM art window rect (viewport px)
 * @param fx/fy  the art's object-position focal (0..1)
 * @param color  type colour for the rim / column
 * @returns { end(now) }
 */
export function forgeHolo(root, d, url, rect, fx, fy, color) {
  let dead = false, raf = 0, h = null, renderer = null, lowering = false, endAt = 0;
  const canvas = document.createElement('canvas');
  canvas.className = 'fg-holo';
  root.appendChild(canvas);
  const W = innerWidth, H = innerHeight;
  const scene = new THREE.Scene();
  const cam = new THREE.PerspectiveCamera(30, W / H, 0.1, 100);
  cam.position.z = 10;
  const ppu = H / (2 * 10 * Math.tan(15 * Math.PI / 180));      // px per world unit on z = 0
  const group = new THREE.Group();
  group.position.set((rect.left + rect.width / 2 - W / 2) / ppu, -(rect.top + rect.height / 2 - H / 2) / ppu, 0);
  group.scale.set(rect.width / ppu / ART_W, rect.height / ppu / ART_H, rect.height / ppu / ART_H);
  scene.add(group);

  const fail = () => end(true);
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.decoding = 'async';
  img.onerror = fail;
  img.onload = async () => {
    if (dead) return;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
      renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.5));
      renderer.setSize(W, H, false);
      renderer.setClearColor(0x000000, 0);
      const tex = new THREE.Texture(img);
      tex.colorSpace = THREE.SRGBColorSpace;
      // object-fit: cover at the card's focal point, as the <img> shows it
      const ia = img.naturalWidth / img.naturalHeight, wa = rect.width / rect.height;
      if (ia > wa) { tex.repeat.set(wa / ia, 1); tex.offset.set((1 - wa / ia) * fx, 0); }
      else { tex.repeat.set(1, ia / wa); tex.offset.set(0, (1 - ia / wa) * (1 - fy)); }
      tex.needsUpdate = true;
      const artMat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0, depthWrite: false });
      const artPlane = new THREE.Mesh(new THREE.PlaneGeometry(ART_W, ART_H), artMat);
      artPlane.visible = false;                 // the DOM card shows the art; only the hologram is drawn here
      group.add(artPlane);
      const mod = await import('./holo/holo2.js?v188');
      if (dead) return;
      const col = new THREE.Color(color);
      h = mod.attachCardHolo2(THREE, { data: d, artPlane, artMat, group }, {
        quality: Math.min(W, H) < 600 ? 'phone' : 'desktop',
        rimColor: [col.r * 1.6, col.g * 1.6, col.b * 1.6], style: 'peel', riseMs: 1500, settleMs: 900,
        up: 0.26, clear: 0.1, lift: 0.03, relief: 0.08, pop: 0.05, shadow: 1.2, beam: true });
      if (!h) return fail();
      h.ready.then(() => {
        if (dead) return;
        if (!lowering) h.lift(true);
        canvas.classList.add('on');
      }, fail);
      let last = performance.now();
      const loop = (now) => {
        if (dead) return;
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        h.update(dt, now / 1000);
        renderer.render(scene, cam);
        // lowered (or never rose): tidy up
        if (lowering && (!h.active || now > endAt)) { end(true); return; }
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    } catch { fail(); }
  };
  img.src = url;

  function end(now = false) {
    if (dead) return;
    if (!now && h) {
      lowering = true;
      endAt = performance.now() + 1400;
      try { h.lift(false); } catch { /* not ready: the loop ends it */ }
      if (!raf) end(true);
      return;
    }
    dead = true;
    cancelAnimationFrame(raf);
    try { h?.dispose(); } catch { /* already gone */ }
    if (renderer) { renderer.dispose(); renderer.forceContextLoss(); }
    canvas.remove();
  }
  return { end };
}
